import { useApp, useInput, useUpdateWidgetValue } from '@libs/hooks';
import { ActionActionTypeEnum, AssetsSvgMap, TWidgetProps } from '@libs/types';
import React, { useId } from 'react';
import { TConsumerItem, TInputSearchAddress, TInputSearchAddressItem } from '../../types/data';
import { RowEnum } from '../../types/enums';
import { TInputOption } from '../../types/options';
import { convertItemToRowComponentProps } from '../../utils/helpers';
import { ButtonRow } from '../Buttons/ButtonRow';
import { TListItemProps } from '../List/ListItems';
import ListSearch from '../List/select/ListSearch';
import RowComponent from '../UI/Row/RowComponent';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';
import { Modal } from '../UI/Modal/Modal';

const InputSearchAddress = ({ widget }: TWidgetProps<TInputSearchAddress, TInputOption>) => {
    const id = useId();
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const { isFilled, isError, errorMessage, value, isDisabled, isHidden, ref } = useInput(widget);
    const { setModal } = useApp();
    const { updateWidgetValue, updateWidgetValueViaAction } = useUpdateWidgetValue(widget);

    const renderItem = ({ item }: TListItemProps) => {
        return (
            item && (
                <RowComponent
                    key={item.id}
                    {...convertItemToRowComponentProps(item)}
                    handleClick={() => itemSelected(item as TInputSearchAddressItem)}
                    id={RowEnum.ROW_REGULAR}
                />
            )
        );
    };

    const selectCurrentValue = (item: TConsumerItem) => (
        <ButtonRow
            isWidget={false}
            isHide={false}
            disabled={false}
            as={'button'}
            handleClick={() => itemSelected(item as TInputSearchAddressItem)}
            label={data.messages?.continueButtonTitle}
        />
    );

    const itemSelected = (item: TInputSearchAddressItem) => {
        updateWidgetValue(item?.clearTitle ?? item.value ?? '');
        if (item?.details) {
            Object.keys(item.details).forEach((key) => updateWidgetValueViaAction(key, ActionActionTypeEnum.onFill, item.details[key] as string));
        }
        setModal(null);
    };

    const modalContent = () => {
        return (
            <Modal isCanCloseModal={false} noTopPadding iconName={AssetsSvgMap.ic_arrow_back}>
                <ListSearch
                    widget={widget}
                    renderItem={renderItem}
                    closeModal={() => setModal(null)}
                    selectCurrentValue={selectCurrentValue}
                    minQueryLength={data.minQueryLength}
                    searchOnServer
                />
            </Modal>
        );
    };
    return (
        <InputWrapper
            isDropdownInput
            htmlFor={id}
            widget={widget}
            labelText={data.label}
            isError={isError}
            isFilled={isFilled}
            isDisabled={isDisabled}
            isHidden={isHidden}
            helperText={data.helperText}
            errorMessage={errorMessage}
            marginTop={options.margin?.top}
            marginRight={options.margin?.right}
            marginBottom={options.margin?.bottom}
            marginLeft={options.margin?.left}
        >
            <input
                ref={ref}
                id={id}
                value={value}
                readOnly={true}
                onClick={() => setModal(modalContent)}
                onFocus={() => setModal(modalContent)}
                data-test={widget.uid}
                type="text"
                tabIndex={data.tabIndex}
            />
        </InputWrapper>
    );
};

export { InputSearchAddress };
